/** @type {Record<string, string>} */
const HIGHLIGHT_BG_CLASS = {
  vert: "bg-[linear-gradient(theme(colors.vert),theme(colors.vert))]",
  "vert-50":
    "bg-[linear-gradient(theme(colors.vert-50),theme(colors.vert-50))]",
  bleu: "bg-[linear-gradient(theme(colors.bleu),theme(colors.bleu))]",
  "bleu-50":
    "bg-[linear-gradient(theme(colors.bleu-50),theme(colors.bleu-50))]",
  rouge: "bg-[linear-gradient(theme(colors.rouge),theme(colors.rouge))]",
  "rouge-50":
    "bg-[linear-gradient(theme(colors.rouge-50),theme(colors.rouge-50))]",
  beige: "bg-[linear-gradient(theme(colors.beige),theme(colors.beige))]",
  "beige-30":
    "bg-[linear-gradient(theme(colors.beige-30),theme(colors.beige-30))]",
  blanc: "bg-[linear-gradient(theme(colors.blanc),theme(colors.blanc))]",
  noir: "bg-[linear-gradient(theme(colors.noir),theme(colors.noir))]",
};

const HIGHLIGHT_BASE_CLASS =
  "inline box-decoration-clone pr-[0.15em] bg-[length:98.5%_60%] bg-[position:60%_60%] bg-no-repeat";

const HIGHLIGHT_LOGO_CLASS =
  "inline box-decoration-clone pr-[0.15em] bg-[length:99.2%_70%] bg-[position:0%_70%] bg-no-repeat";


export const getHighlightClassName = (highlightColor = "vert", type = "default") => {
  const bgClass = HIGHLIGHT_BG_CLASS[highlightColor] ?? HIGHLIGHT_BG_CLASS.vert;
  if (type === "logo") {
    return `${HIGHLIGHT_LOGO_CLASS} ${bgClass}`;
  }
  return `${HIGHLIGHT_BASE_CLASS} ${bgClass}`;
};

export const getTitleComponents = ({ color, highlightColor = "vert", className = "" } = {}) => ({
  heading1: ({ children }) => (
    <h1 className={`h1 md:h1-desktop text-${color || "bleu"} ${className}`}>{children}</h1>
  ),
  heading2: ({ children }) => (
    <h2 className={`h2 md:h2-desktop text-${color || "bleu"} ${className}`}>{children}</h2>
  ),
  label: ({ node, children }) => {
    if (node.data.label === "highlight") {
      return (
        <span className={`italic ${getHighlightClassName(highlightColor)}`}>
          {children}
        </span>
      );
    }

    return <span>{children}</span>;
  },
});

export const getSubTitleComponents = ({ color = "bleu", uppercase = false } = {}) => ({
  heading2: ({ children }) => <h2 className={`p-xl text-${color} ${uppercase ? "uppercase" : ""}`}>{children}</h2>,
  heading3: ({ children }) => <h3 className={`p-xl text-${color} ${uppercase ? "uppercase" : ""}`}>{children}</h3>,
  heading4: ({ children }) => <h4 className={`p-xl text-${color} ${uppercase ? "uppercase" : ""}`}>{children}</h4>,
  paragraph: ({ children }) => <p className={`p-xl text-${color} ${uppercase ? "uppercase" : ""}`}>{children}</p>,
});

export const getTextComponents = ({ variation = 'lg', color = "bleu", is_bold = false, className = "" } = {}) => ({
  paragraph: ({ children }) => (
    <p className={`${className} text-${color} ${is_bold ? "font-medium" : ""} ${variation === "sm" ? "p-base md:p-lg" : variation === "lg" ? "p-lg" : "p-lg md:p-xl"}`}>
      {children}
    </p>
  ),
  heading1: ({ children }) => <p
    className={`${className} text-${color} ${is_bold ? "font-medium" : ""} ${variation === "sm" ? "p-base md:p-lg" : variation === "lg" ? "p-lg" : "p-lg md:p-xl"}`}
  >
    {children}
  </p>,
  heading2: ({ children }) => <p
    className={`${className} text-${color} ${is_bold ? "font-medium" : ""} ${variation === "sm" ? "p-base md:p-lg" : variation === "lg" ? "p-lg" : "p-lg md:p-xl"}`}
  >
    {children}
  </p>,
  strong: ({ children }) => <strong className="font-semibold">{children}</strong>,
  em: ({ children }) => <em>{children}</em>,
  hyperlink: ({ children, node }) => (
    <a
      href={node.data.url}
      className={`text-${color} underline underline-offset-2`}
    >
      {children}
    </a>
  ),
});

export const getHightlightedTextComponents = ({ className = "", highlightColor = "beige-30" } = {}) => ({
  heading3: ({ children }) => (
    <h3 className={`h3 text-bleu ${className}`}>
      {children}
    </h3>
  ),
  paragraph: ({ children }) => (
    <p className={`${className}`}>
      {children}
    </p>
  ),
  label: ({ node, children }) => {
    if (node.data.label === "highlight") {
      return (
        <span className={`italic ${getHighlightClassName(highlightColor)}`}>
          {children}
        </span>
      );
    }

    return <span>{children}</span>;
  },
});


/** Slug used as heading id (article body) and data-pointer (sidemenu): both must match. */
export const textToSlug = (text) =>
  text
    .split(" ")
    .join("-")
    .split("'")
    .join("-")
    .split(":")
    .join("")
    .split("(")
    .join("")
    .split(")")
    .join("")
    .split("--")
    .join("-");

export const getBlogComponents = ({ highlightColor = "beige", color = "bleu" } = {}) => ({
  heading2: ({ children, text }) => <h2 className={`h2-n lg:dk bd  text-bleu sumup-trigger mb-7`} id={textToSlug(text)}>{children}</h2>,
  heading3: ({ children }) => <h3 className={`h3-n lg:dk text-bleu mb-8`}>{children}</h3>,
  heading4: ({ children }) => <h4 className={`h4 text-bleu`}>{children}</h4>,
  paragraph: ({ children }) => <p className={`text-${color} p-blog my-5 first:mt-0 last:mb-0`}>{children}</p>,
  strong: ({ children }) => <strong className="font-semibold">{children}</strong>,
  label: ({ node, children }) => {
    if (node.data.label === "highlight") {
      return (
        <span className={`italic ${getHighlightClassName(highlightColor)} font-medium`}>
          {children}
        </span>
      );
    }
    if (node.data.label === "quote") {
      return (
        <span className={`block text-[22px] font-medium text-bleu pl-5 py-2.5 border-l-[2px] border-vert leading-none`}>
          {children}
        </span>
      );
    }
    return <span>{children}</span>;
  },
  em: ({ children }) => <em>{children}</em>,
  list: ({ children }) => (
    <ul className="text-bleu p-blog list-blog">
      {children}
    </ul>
  ),
  listItem: ({ children }) => (
    <li>
      {children}
    </li>
  ),
  hyperlink: ({ children, node }) => (
    <a
      href={node.data.url}
      className={`text-${color} underline underline-offset-2`}
    >
      {children}
    </a>
  ),
});

const MONTHS = ['Janvier', 'Février', 'Mars', 'Avril', 'Mai', 'Juin', 'Juillet', 'Août', 'Septembre', 'Octobre', 'Novembre', 'Décembre'];

export const parseDate = (date) => {
  const month = MONTHS[new Date(date).getMonth()];
  return `${new Date(date).getDate()} ${month} ${new Date(date).getFullYear()}`;
}